import { useEffect, useState } from "react";
import type { RuleFormData } from "../../components/forms/BuildingForm";
import { createRule, deleteRule, getRule, patchRuleStatus, updateRule } from "../../services/ruleManagement/ruleManagement.service";
import type { RuleDetail, RuleSummary } from "../../services/ruleManagement/ruleManagement.types";
import {
  buildRulePayload, buildRuleUpdatePayload, getBuildingErrorMessage, loadBuildingRules,
  mapRuleDetailToFormData, mapRuleDetailToSummary,
} from "./buildingRules";
import type { ConfirmBuildingAction, CustomerWithSite } from "./buildingRules";

interface Props {
  customer: CustomerWithSite | null;
  confirm: ConfirmBuildingAction;
  onChanged: () => Promise<void>;
  onError: (message: string | null) => void;
  onSuccess: (message: string | null) => void;
}

export function useBuildingRules({ customer, confirm, onChanged, onError, onSuccess }: Props) {
  const [rules, setRules] = useState<RuleSummary[]>([]);
  const [details, setDetails] = useState<Record<number, RuleDetail>>({});
  const [loading, setLoading] = useState(Boolean(customer?.siteId));
  const [submitting, setSubmitting] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [editingRuleData, setEditingRuleData] = useState<RuleFormData | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(Boolean(customer?.siteId));
    loadBuildingRules(customer?.siteId).then(rules => {
      if (mounted) setRules(rules);
    }).catch((error: unknown) => {
      if (mounted) onError(getBuildingErrorMessage(error));
    }).finally(() => {
      if (mounted) setLoading(false);
    });
    return () => { mounted = false; };
  }, [customer?.siteId, onError]);

  const cancelEdit = () => {
    setEditingRuleId(null);
    setEditingRuleData(null);
  };

  const edit = async (ruleId: number) => {
    onError(null);
    onSuccess(null);
    try {
      const response = await getRule(ruleId);
      if (response.success && response.data) {
        const detail = response.data;
        setDetails(previous => ({ ...previous, [ruleId]: detail }));
        setEditingRuleData(mapRuleDetailToFormData({ ...detail, isActive: detail.isActive ?? true }, customer?.siteId));
      } else if (details[ruleId]) {
        setEditingRuleData(mapRuleDetailToFormData(details[ruleId], customer?.siteId));
        onSuccess("Loaded local rule data; server details unavailable (rule may be deactivated).");
      } else {
        throw new Error(response.error?.message || "Failed to load rule details");
      }
      setEditingRuleId(ruleId);
    } catch (error: unknown) {
      onError(getBuildingErrorMessage(error));
    }
  };

  const update = async (ruleId: number, data: RuleFormData): Promise<boolean> => {
    onError(null);
    onSuccess(null);
    setSubmitting(true);
    try {
      if (!customer?.siteHierarchyId) throw new Error("Site hierarchy ID is missing");
      const response = await updateRule(ruleId, buildRuleUpdatePayload({ ...data, ruleName: data.ruleName || `Rule ${ruleId}` }));
      if (!response.success || !response.data) throw new Error(response.error?.message || "Failed to update rule");
      const updated = response.data;
      setDetails(previous => ({ ...previous, [ruleId]: updated }));
      setRules(previous => previous.map(rule => rule.siteRuleId === ruleId ? mapRuleDetailToSummary(updated) : rule));
      cancelEdit();
      onSuccess("Rule updated successfully");
      await onChanged();
      return true;
    } catch (error: unknown) {
      onError(getBuildingErrorMessage(error));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const remove = (ruleId: number) => {
    const rule = rules.find(item => item.siteRuleId === ruleId);
    const active = Boolean(rule?.isActive);
    confirm(active ? "Deactivate Rule" : "Delete Rule",
      active ? "Are you sure you want to deactivate this rule?" : "Are you sure you want to delete this rule? This action cannot be undone.",
      async () => {
        onError(null);
        onSuccess(null);
        setSubmitting(true);
        try {
          const response = await deleteRule(ruleId);
          if (!response.success && (active || response.error?.status !== 404)) {
            throw new Error(response.error?.message || "Failed to deactivate/delete rule");
          }
          setRules(previous => active
            ? previous.map(item => item.siteRuleId === ruleId ? { ...item, isActive: false } : item)
            : previous.filter(item => item.siteRuleId !== ruleId));
          if (active) {
            setDetails(previous => previous[ruleId]
              ? { ...previous, [ruleId]: { ...previous[ruleId], isActive: false } } : previous);
          } else {
            setDetails(previous => {
              const next = { ...previous };
              delete next[ruleId];
              return next;
            });
            if (editingRuleId === ruleId) cancelEdit();
          }
          onSuccess(active ? "Rule deactivated successfully" : response.success ? "Rule deleted" : "Rule removed");
          await onChanged();
        } catch (error: unknown) {
          onError(getBuildingErrorMessage(error));
        } finally {
          setSubmitting(false);
        }
      });
  };

  const activate = (ruleId: number) => {
    confirm("Activate Rule", "Are you sure you want to activate this rule?", async () => {
      onError(null);
      onSuccess(null);
      setSubmitting(true);
      try {
        const response = await patchRuleStatus(ruleId, { isActive: true });
        if (response.success) {
          setRules(previous => previous.map(rule => rule.siteRuleId === ruleId ? { ...rule, isActive: true } : rule));
          setDetails(previous => previous[ruleId]
            ? { ...previous, [ruleId]: { ...previous[ruleId], isActive: true } } : previous);
          onSuccess("Rule activated successfully");
        } else if (response.error?.status === 404 && details[ruleId] && customer?.siteId && customer.siteHierarchyId) {
          const payload = buildRulePayload(customer.siteId, customer.siteHierarchyId, {
            ...mapRuleDetailToFormData(details[ruleId], customer.siteId), isActive: true,
          });
          const created = await createRule(payload);
          if (!created.success || !created.data) throw new Error(created.error?.message || "Failed to recreate rule");
          const recreated = created.data;
          setRules(previous => previous.map(rule => rule.siteRuleId === ruleId ? mapRuleDetailToSummary(recreated) : rule));
          setDetails(previous => ({ ...previous, [recreated.siteRuleId]: recreated }));
          onSuccess("Rule recreated and activated");
        } else {
          throw new Error(response.error?.message || "Failed to activate rule");
        }
        await onChanged();
      } catch (error: unknown) {
        onError(getBuildingErrorMessage(error));
      } finally {
        setSubmitting(false);
      }
    });
  };

  return {
    rules, details, setDetails, loading, submitting, editingRuleId, editingRuleData,
    setEditingRuleData, edit, update, remove, activate, cancelEdit,
  };
}
